import { IconForms, IconMailOff } from "@tabler/icons-react"
import { type FC } from "react"
import { Navigate } from "react-router"

import { type ThinLeadFormEmail } from "@forento/core/models/studentEmail"
import Tooltip from "@forento/ui/components/Tooltip"
import { cn } from "@forento/ui/utilities/tailwind"

import Button from "~/components/Button"
import { DeleteIcon, EditIcon } from "~/components/Icon"
import Layout, { PageBreadcrumb, PageHeader } from "~/components/Layout"
import Link from "~/components/Link"
import PartialLoadingPage from "~/components/PartialLoadingPage"
import { useAlert } from "~/contexts/AlertContext"
import { usePlatform } from "~/contexts/UserContext"
import routes from "~/utilities/routes"
import { query } from "~/utilities/trpc"

const kindLabels: Record<NonNullable<ThinLeadFormEmail["form"]>["kind"], string> = {
	newsletter: "Newsletter form",
	inquiry: "Inquiry form",
}

const LeadFormEmails: FC = () => {
	const platform = usePlatform()!
	const alert = useAlert()

	const { data: emails, error, refetch } = query.studentEmails.listLeadForm.useQuery()

	const deleteEmail = query.studentEmails.deleteLeadForm.useMutation({
		async onSuccess() {
			await refetch()
		},
		async onError(error) {
			console.error(error)
			await alert.show("Error", "Failed to delete the reply email. Please try again.")
		},
	})

	if (!platform.plan.access.landingPage) {
		return <Navigate to={routes.settings.studentEmail.index()} replace />
	}

	return (
		<Layout>
			<PageHeader>
				<PageBreadcrumb
					title="Website form replies"
					path={[
						{ title: "Settings", link: routes.settings.index() },
						{ title: "Email Automation", link: routes.settings.studentEmail.index() },
					]}
				/>
			</PageHeader>
			{error ? (
				<p>Failed to load website forms.</p>
			) : emails === undefined ? (
				<PartialLoadingPage />
			) : emails.length === 0 ? (
				<div className="border-component-border bg-component-background flex flex-col items-start gap-4 rounded-3xl border p-6">
					<p>
						Your website has no forms yet. Add a form block in the website editor, and it shows up here so
						you can write the email each submitter receives.
					</p>
					<Button variant="primary" onClick={routes.main.websiteEditor()}>
						Open website editor
					</Button>
				</div>
			) : (
				<div className="flex flex-col gap-4">
					<p className="text-gray-600">
						Each form on your website can send its own reply the moment someone submits it: a confirmation,
						next steps, or the content you promised.
					</p>
					{emails.map(item => (
						<div
							key={item.formKey}
							className="border-component-border bg-component-background flex items-center gap-4 rounded-3xl border p-5"
						>
							{item.form ? (
								<IconForms className="size-6 shrink-0" />
							) : (
								<IconMailOff className="size-6 shrink-0 text-gray-500" />
							)}
							<div className="flex min-w-0 grow flex-col gap-1">
								<h2 className="truncate text-xl font-semibold">
									{item.form?.title ?? "Form removed from the website"}
								</h2>
								<p className="text-sm text-gray-600">
									{item.form
										? `${kindLabels[item.form.kind]} on ${item.form.pages.map(page => page.title).join(", ")}`
										: "This form no longer exists on your website, so its reply email is not sent."}
								</p>
							</div>
							<span
								className={cn(
									"shrink-0 rounded-full px-3 py-1 text-xs font-medium",
									item.email?.hasContent
										? "bg-emerald-50 text-emerald-800"
										: "bg-gray-100 text-gray-600",
								)}
							>
								{item.email?.hasContent ? "Reply email on" : "No reply email"}
							</span>
							<div className="flex shrink-0 items-center gap-4">
								<Tooltip tooltip={item.email ? "Edit reply email" : "Set up reply email"}>
									<Link to={routes.settings.studentEmail.editLeadForm(item.formKey)}>
										<EditIcon className="size-6" />
									</Link>
								</Tooltip>
								{item.email && (
									<Tooltip tooltip="Delete reply email">
										<button
											type="button"
											className="cursor-pointer"
											onClick={async () => {
												const dialog = await alert.confirm(
													"Delete reply email",
													"Are you sure you want to delete this reply email? Submitters of this form will no longer receive it.",
												)
												if (!dialog.result) return
												await deleteEmail.mutateAsync(item.formKey)
												dialog.close()
											}}
										>
											<DeleteIcon className="text-danger size-6" />
										</button>
									</Tooltip>
								)}
							</div>
						</div>
					))}
				</div>
			)}
		</Layout>
	)
}

export default LeadFormEmails
